import React, { Fragment, memo } from 'react'
import tw from 'twin.macro'
import { Button } from 'src/components/shared/Button'
import { CommonModal } from 'src/components/shared/CommonModal'
import { EditTemplateTitleForm } from './EditTemplateTitleForm'
import type { EditTemplateTitleFormValues } from './EditTemplateTitleForm.interface'

const FORM_ID = 'edit-template-title-form'

interface Props {
  isOpen: boolean
  name: string
  isSaving: boolean
  /** Defaults to the rename wording; new-template mode names a template that does not exist yet. */
  title?: string
  submitLabel?: string
  onSubmit(values: EditTemplateTitleFormValues): void
  onClose(): void
}

/**
 * Names a template in both editor modes; the caller decides what confirming does.
 *
 * Renaming an existing template is deliberately its own request — it is NOT part of the editor's
 * "Save changes" diff. The template name is not expressible as a step/group action (the
 * backend's `CollectDataTemplateUpdateDto` accepts those only), so it goes through the separate
 * ops-tools rename endpoint and lands the moment this modal is confirmed.
 *
 * Naming a new template cannot use that endpoint — it takes an id that does not exist yet — so
 * there the caller applies the name to the draft and it lands with the create request instead.
 * Hence the overridable `title` / `submitLabel`, which default to the rename wording.
 */
const EditTemplateTitleModalComponent = ({
  isOpen,
  name,
  isSaving,
  title = 'Rename template',
  submitLabel = 'Save name',
  onSubmit,
  onClose,
}: Props) => {
  return (
    <CommonModal
      isOpen={isOpen}
      title={title}
      // Wider than the default confirm-dialog width: this modal carries a form.
      className={tw`max-w-[480px]`}
      onClose={onClose}
      buttons={
        <Fragment>
          <Button size="small" color="grey" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="small"
            color="primary"
            type="submit"
            form={FORM_ID}
            loading={isSaving}
            disabled={isSaving}
            data-testid="edit-template-title-submit-button"
          >
            {submitLabel}
          </Button>
        </Fragment>
      }
    >
      <EditTemplateTitleForm formId={FORM_ID} name={name} onSubmit={onSubmit} />
    </CommonModal>
  )
}

export const EditTemplateTitleModal = memo(EditTemplateTitleModalComponent)
